import { ChangeEvent } from 'react';
import { useSelector } from 'react-redux';
import intl from 'react-intl-universal';
import get from 'lodash/get';
import {
  Typography,
  FormItem,
  TextField,
  TextArea,
} from '@getsynapse/design-system';
import { selectUser } from 'state/User/userSlice';
import { TaskBundle } from 'utils/customTypes';
import UserAvatar from 'Atoms/UserAvatar';
import { DeletedUser } from 'Atoms/DeletedUser';
import { useTaskBundleDraft } from '../providers/TaskBundleProvider/TaskBundleProvider';

type CreatorDisplay = {
  firstName: string;
  lastName: string;
  avatar_url: string;
};

const EMPTY_CREATOR: CreatorDisplay = {
  avatar_url: '',
  firstName: '',
  lastName: '',
};

type BundleOverviewProps = {
  isEditing?: boolean;
};

const BundleOverview = ({ isEditing }: BundleOverviewProps) => {
  const currentUser = useSelector(selectUser);
  const { selectors, actions } = useTaskBundleDraft();
  const data = selectors.draft.bundle as TaskBundle;

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    actions.updateBundleField('name', event.target.value);
  };

  const handleDescriptionChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    actions.updateBundleField('description', event.target.value);
  };

  const getCreatorForDisplay = (): CreatorDisplay => {
    if (!isEditing) {
      return {
        firstName: currentUser?.firstName ?? '',
        lastName: currentUser?.lastName ?? '',
        avatar_url: currentUser?.avatar_url ?? '',
      };
    }

    if (!data?.taskBundleCreator) {
      return EMPTY_CREATOR;
    }

    const creator = data.taskBundleCreator;
    const { firstName = '', lastName = '' } = creator.data ?? {};

    return {
      avatar_url: get(data, 'taskBundleCreator.avatar_url', ''),
      firstName,
      lastName,
    };
  };

  const { firstName, lastName, avatar_url } = getCreatorForDisplay();
  const hasCreator = firstName && lastName;

  return (
    <div className='mt-6'>
      <Typography variant='body2' className='text-neutral-dark'>
        {intl.get(
          'SETTINGS_PAGE.TASK_BUNDLE_PAGE.OVERVIEW_SECTION.SECTION_DESCRIPTION'
        )}
      </Typography>
      <div className='grid grid-cols-2 mt-6 gap-y-6 gap-x-10%'>
        <FormItem
          label={intl.get(
            'SETTINGS_PAGE.TASK_BUNDLE_PAGE.OVERVIEW_SECTION.TITLE_LABEL'
          )}
          labelProps={{ required: true }}
        >
          <TextField
            value={data?.name || ''}
            data-testid='build-bundle-name-input'
            onChange={handleNameChange}
            data-cy='task-bundle-title_input'
            aria-label='bundle-overview-title-input'
          />
        </FormItem>
        <FormItem
          label={intl.get(
            'SETTINGS_PAGE.TASK_BUNDLE_PAGE.OVERVIEW_SECTION.CREATOR_LABEL'
          )}
        >
          {hasCreator ? (
            <div className='flex items-center'>
              <UserAvatar
                className='mr-2 pointer-events-none'
                user={{
                  avatar_url,
                  data: {
                    firstName,
                    lastName,
                  },
                }}
              />
              <Typography variant='body2' className='text-neutral-black'>
                {firstName} {lastName}
              </Typography>
            </div>
          ) : (
            <DeletedUser />
          )}
        </FormItem>
        <FormItem
          label={intl.get(
            'SETTINGS_PAGE.TASK_BUNDLE_PAGE.OVERVIEW_SECTION.DESCRIPTION_LABEL'
          )}
          labelProps={{ required: true }}
        >
          <TextArea
            textAreaProps={{
              'data-cy': 'task-bundle-description_input',
              'data-testid': 'build-bundle-description-input',
              'aria-label': 'bundle-overview-description-input',
            }}
            value={data?.description || ''}
            onChange={handleDescriptionChange}
          />
        </FormItem>
      </div>
    </div>
  );
};

export default BundleOverview;
